import React from 'react';

import {LockOutlined} from '@ant-design/icons/lib';
import {Button, Card} from 'antd';
import {confirmSignIn, rememberDevice, signIn} from 'aws-amplify/auth';
import type {FormikProps} from 'formik';
import {withFormik} from 'formik';
import {Checkbox, Form, FormItem, Input} from 'formik-antd';
import * as yup from 'yup';

import {AnchorLink} from '../AnchorLink';

import {Description, Extra} from './Shared';
import {fetchCurrentUser} from './utils';

import {useRecaptcha} from 'hooks/useRecaptcha';
import {getErrorInfo} from 'lib/errorUtils';
import type {IAuthProps} from 'types';
import {AuthState, MfaType} from 'types';

interface IFormValues {
  code: string;
  remember: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
const C: React.FC<FormikProps<IFormValues> & IAuthProps> = ({
  isSubmitting,
  setState,
  mfaDestination,
  email,
  password,
  mfaType,
  setSubmitting
}) => {
  const {executeRecaptcha} = useRecaptcha();

  const backToSignIn = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setState({authState: AuthState.signIn, email});
  };

  const resendCode = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    // Check if we have the required credentials
    if (!email || !password) {
      setState({authState: AuthState.signIn, email});
      return;
    }

    setSubmitting(true);
    try {
      const recaptchaToken = await executeRecaptcha();
      const userAgent = navigator.userAgent || '';

      if (!recaptchaToken) {
        setState({authState: AuthState.signIn, email});
        return;
      }

      // In Amplify v6, re-calling signIn is the way to resend MFA code
      await signIn({
        username: email,
        password,
        options: {
          clientMetadata: {
            recaptchaToken,
            userAgent
          }
        }
      });
    } catch {
      setState({authState: AuthState.signIn, email});
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      title="Two-step authentication"
      extra={<AnchorLink onClick={backToSignIn}>Back to Sign In</AnchorLink>}>
      <Description>
        {mfaType === MfaType.SMS_MFA ? (
          <>
            To continue, please enter the 6-digit verification code sent to{' '}
            <strong>{mfaDestination}</strong>.
          </>
        ) : (
          <>
            To continue, please enter the 6-digit verification code generated by your{' '}
            <strong>authenticator app</strong>.
          </>
        )}
      </Description>
      <Form layout="vertical">
        <FormItem name="code" label="Verification code">
          <Input
            name="code"
            type="text"
            size="large"
            autoFocus
            autoComplete="one-time-code"
            prefix={<LockOutlined />}
          />
        </FormItem>
        <p>
          <Checkbox name="remember">Remember this device?</Checkbox>
        </p>
        <Button block size="large" type="primary" loading={isSubmitting} htmlType="submit">
          Confirm
        </Button>
        <Extra>
          Lost your code?{' '}
          <AnchorLink
            onClick={(e: React.SyntheticEvent) => {
              void resendCode(e);
            }}>
            Resend Code
          </AnchorLink>
        </Extra>
      </Form>
    </Card>
  );
};

export const ConfirmSignIn = withFormik<IAuthProps, IFormValues>({
  validationSchema: () =>
    yup.object().shape({
      code: yup.string().required()
    }),
  mapPropsToValues: ({code = ''}) => ({code, remember: true}),
  handleSubmit: async ({code, remember}, {props, setErrors, setSubmitting}) => {
    try {
      const {isSignedIn} = await confirmSignIn({challengeResponse: code});
      if (isSignedIn) {
        // Only remember device after successful sign-in
        if (remember) {
          try {
            await rememberDevice();
          } catch (err) {
            // Ignore remember device errors - not critical for auth flow
            console.error('Failed to remember device:', err);
          }
        }
        const currentUser = await fetchCurrentUser();
        if (currentUser) {
          props.setState({authState: AuthState.signedIn, currentUser});
        }
      }
    } catch (error: unknown) {
      // Safely extract error information
      const {message: errorMessage} = getErrorInfo(error);

      setErrors({code: errorMessage});
    } finally {
      setSubmitting(false);
    }
  }
})(C);
